import React, {ReactNode, useCallback, useMemo, useState} from 'react'
import {FormSection} from "../../../common/components/SectionedForm";
import {api, buildPaginatedUrl} from "../../../common/api";
import {useTranslation} from "react-i18next";
import {PhoneBookEntry, ServerPage} from "../../../app/types";
import {Dialog, DialogActions, DialogContent, DialogTitle, Grid, ListItemIcon, ListItemText, Menu} from "@mui/material";
import {Sort, useInfiniteScroll} from "../../../common/components/table/useInfiniteScroll";
import {useAccessHelpers, useUrlHelpers} from "../../../common/util/hooks";
import {ColumnDefinition, InfiniteScrollTable} from "../../../common/components/table/Table";
import TableRowActionsMenu from "../../../common/components/table/TableRowActionsMenu";
import {sorterHeader} from "../../../common/components/table/SorterHeader";
import {AddButton} from "../../../common/components/AddButton";
import CancelButton from "../../../common/form/CancelButton";
import ErrorAlert from "../../../common/components/ErrorAlert";
import DeletionModal from "../../../common/components/DeletionModal";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import IconButton from "@mui/material/IconButton";
import SettingsIcon from "@mui/icons-material/Settings";
import MenuItem from "@mui/material/MenuItem";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import CloseIcon from "@mui/icons-material/Close";
import FileDropZone from "../../../common/components/upload/FileDropZone";
import usePhoneBookForm from "../../../phonebook/usePhoneBookForm";
import {useOktaAuth} from "@okta/okta-react";
import {MAX_CSV_FILE_SIZE} from "../../../phonebook";

export const useDirectorySection = () : FormSection<PhoneBookEntry[]> => {
    const { t } = useTranslation()
    const {createUrl} = useUrlHelpers();
    const [entryToDelete, setEntryToDelete] = useState<PhoneBookEntry | null>(null)
    const [entryToUpdate, setEntryToUpdate] = useState<PhoneBookEntry | null>(null)
    const {hasWriteAccess, onlyWithWriteAccess} = useAccessHelpers()
    const [openImportCsvDialog, setOpenImportCsvDialog] = useState<boolean>(false)

    const fetchEntries = useCallback(
        async (pageParam = 0, sortParam: Sort, searchTermParam: string | null): Promise<ServerPage<PhoneBookEntry>> => {
            const { data } = await api.get(buildPaginatedUrl(createUrl(`business-reporting/directory`), pageParam, sortParam, searchTermParam, 10000))
            return data
        },
        [createUrl]
    )

    const { sort, setSort, rows, error, fetchNextPage, hasNextPage, isFetching, refetch } = useInfiniteScroll<PhoneBookEntry>({
        queryKey: `business-reporting-directory`,
        initialSort: { field: 'name', order: 'asc' },
        fetchFunction: fetchEntries,
    })

    const onSubmit = useCallback(
        async (formData: PhoneBookEntry) => {
            const url = createUrl(`business-reporting/directory`)
            await api.post(url, formData)
            setEntryToUpdate(null)
            await refetch()
        },
        [refetch, createUrl]
    )

    const { handleSubmit, form: Form, submitButton: SubmitButton, reset } = usePhoneBookForm({ entry: entryToUpdate, onSubmit })

    const rowActions = useMemo(
        () => [
            {
                key: 'delete',
                text: t('general.delete'),
                onClick: (record: PhoneBookEntry) => {
                    setEntryToDelete(record)
                },
                needsWriteAccess: true
            },
        ],
        [t]
    )

    const columns: ColumnDefinition<PhoneBookEntry>[] = useMemo(
        () => [
            {
                title: t('phonebook.table.name'),
                key: 'name',
                sorter: true,
                render: (text: string) => <>{text}</>,
            },
            {
                title: t('phonebook.table.number'),
                key: 'number',
                sorter: true,
                render: (text: string) => <>{text}</>,
            },
            {
                title: '',
                key: 'uuid',
                sorter: false,
                render: (text: string, record: PhoneBookEntry) => <TableRowActionsMenu record={record} menuItems={rowActions} readOnly={!hasWriteAccess}/>,
            },
        ],
        [t, rowActions, hasWriteAccess]
    )

    const sorter = useCallback(col => sorterHeader(col, sort, setSort), [sort, setSort])

    const getRowKey = useCallback(entry => entry.uuid, [])

    const getDeleteUrl = useCallback(i => i ? createUrl(`business-reporting/directory/${i.uuid}`) : null, [createUrl])

    const oktaAuth = useOktaAuth();

    const [anchorEl, setAnchorEl] = React.useState(null);

    const handleClick = (event: any) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = useCallback(() => {
        setAnchorEl(null);
    }, [setAnchorEl]);

    const buttonPanel = useMemo(
        () => (
            <Grid container item justifyContent={'space-between'} flexDirection={'row'} spacing={2} flexWrap={'nowrap'}>
                {onlyWithWriteAccess(<Grid item>
                    <AddButton
                        onClick={() => {
                            reset()
                            setEntryToUpdate({ name: '', number: '', uuid: '' })
                            return null
                        }}
                        label={`${t('phonebook.create-new')}`}
                    />
                </Grid>)
                }
                <Grid item xs={'auto'} pr={1}>
                    <IconButton color="secondary" onClick={handleClick}>
                        <SettingsIcon/>
                    </IconButton>
                    <Menu
                        id="simple-menu"
                        anchorEl={anchorEl}
                        keepMounted
                        open={Boolean(anchorEl)}
                        onClose={handleClose}
                    >
                        {<MenuItem disabled={!hasWriteAccess} onClick={() => setOpenImportCsvDialog(true)}>
                            <ListItemIcon>
                                <FileDownloadIcon/>
                            </ListItemIcon>
                            <ListItemText>{t("phonebook.import-csv-title")}</ListItemText>
                        </MenuItem>}
                        <MenuItem onClick={async () => {
                            const url = createUrl( `business-reporting/directory/download`)
                            fetch(url, {
                                method: 'GET',
                                headers: {
                                    ...(oktaAuth?.authState?.isAuthenticated? {"authorization" : `Bearer ${oktaAuth?.authState?.accessToken?.accessToken}`} : {})
                                },
                            })
                                .then(response => response.blob())
                                .then(blob => {
                                    // Create blob link to download
                                    const url = window.URL.createObjectURL(new Blob([blob]))
                                    const link = document.createElement('a')
                                    link.href = url
                                    link.setAttribute('download', `business_reporting_directory.csv`)

                                    document.body.appendChild(link)
                                    link.click()
                                    link.parentNode?.removeChild(link)
                                })
                        }}>
                            <ListItemIcon>
                                <FileUploadIcon/>
                            </ListItemIcon>
                            <ListItemText>{t("phonebook.export-csv-title")}</ListItemText>
                        </MenuItem>
                    </Menu>
                </Grid>
            </Grid>
        ),
        [t, reset, createUrl,
            oktaAuth?.authState?.accessToken?.accessToken, oktaAuth?.authState?.isAuthenticated,
            anchorEl, handleClose, hasWriteAccess, onlyWithWriteAccess
        ]
    )

    const onCsvFileUploaded = useCallback(file => {
        refetch();
        handleClose();
        setOpenImportCsvDialog(false);

    }, [refetch, handleClose])

    const entryForm = useMemo(
        () => (
            <form className={'ant-form ant-form-vertical'} onSubmit={handleSubmit} noValidate={true}>
                <Dialog disablePortal open={!!entryToUpdate} onClose={() => setEntryToUpdate(null)}>
                    <DialogTitle>{t(entryToUpdate?.uuid ? 'phonebook.update-dialog-title' : 'phonebook.create-new')}</DialogTitle>
                    <DialogContent>
                        <Form />
                    </DialogContent>
                    <DialogActions>
                        <SubmitButton />
                        <CancelButton onClick={() => setEntryToUpdate(null)} />
                    </DialogActions>
                </Dialog>
            </form>
        ),
        [Form, SubmitButton, entryToUpdate, handleSubmit, t]
    )

    const importCSVForm = useMemo(
        () => (
            <Dialog open={openImportCsvDialog} onClose={() => setOpenImportCsvDialog(false)}>
                <DialogTitle>
                    {t('phonebook.import-csv-title')}
                    <IconButton
                        aria-label="close"
                        onClick={() => setOpenImportCsvDialog(false)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: theme => theme.palette.grey[500],
                        }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {
                        <Grid container>
                            <Grid item>
                                <FileDropZone
                                    uploadUrl={createUrl(`business-reporting/directory/csv`)}
                                    onFileUploaded={onCsvFileUploaded}
                                    acceptMime=".csv"
                                    maxSizeInBytes={MAX_CSV_FILE_SIZE}
                                    dropzoneTitle={t('phonebook.import-csv-dropzone-title')}
                                    showSuccess={true}
                                />
                            </Grid>
                        </Grid>
                    }
                </DialogContent>
                <DialogActions>
                    <CancelButton title={t('general.close')} onClick={() => setOpenImportCsvDialog(false)} />
                </DialogActions>
            </Dialog>
        ),
        [t, onCsvFileUploaded, openImportCsvDialog, createUrl]
    )

    const renderDetails = useCallback(
        (data: PhoneBookEntry[]): ReactNode => {
            return (
                <Grid container p={1}>
                    <ErrorAlert showAlert={!!error} />
                    <Grid container item justifyContent={'space-between'} flexDirection={'row'} spacing={1} flexWrap={'nowrap'} pb={3}>
                        <Grid item xs={'auto'} pr={1}>
                            <></>
                        </Grid>
                        <Grid item>{buttonPanel}</Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <InfiniteScrollTable<PhoneBookEntry>
                            hasNextPage={hasNextPage}
                            data={rows}
                            fetchNextPage={fetchNextPage}
                            isFetching={isFetching}
                            getRowKey={getRowKey}
                            sorter={sorter}
                            columns={columns}
                            tableId={'userPhonebookTable'}
                        />
                    </Grid>

                    <DeletionModal<PhoneBookEntry | null>
                        nameToDelete={(entryToDelete: any) => entryToDelete?.name || ''}
                        itemToDelete={entryToDelete} deleteUrl={getDeleteUrl} onClose={() => setEntryToDelete(null)} onSuccess={refetch} />

                    {entryForm}
                    {importCSVForm}
                </Grid>
            )
        },
        [ columns, buttonPanel, entryForm, entryToDelete, error, isFetching, importCSVForm,
            fetchNextPage, getDeleteUrl, getRowKey, hasNextPage, refetch, rows, sorter]
    )

    return {
        title: t("business-reporting-directory.title"),
        data: rows,
        isFetching: isFetching || false,
        error: error,
        refetch: refetch,
        renderDetails: renderDetails,
        renderForm: null,
        enabled: () => true,
        icon: () => <MenuBookIcon/>
    }
}